import {
	type GlobalStoreEvent,
	type GlobalStoreRequest,
	type GlobalStoreSnapshot,
	type QueryStreamRequest,
	type StackPackStateChangeEvent,
	type StatusUpdateRequest,
	type SubjectPermissionsRequest,
	type SubjectPermissionsResponse,
	Type,
	type ViewSnapshotResponse,
	type WebSocketInProtocol,
	type WebSocketOutProtocol,
	type WebSocketStartStream,
	type WebSocketStopStream,
	type WebSocketStreamType,
	type WebUIConfig,
} from "~/models/dto";
import { createURL } from "~/tools/create-url";
import { Lazy } from "~/tools/lazy";
import { Logger } from "~/tools/logger";
import { type Observable, map, tap } from "~/tools/rxjs";
import type { WebSocketSubject } from "~/tools/rxjs/web-socket";

import { createWebSocketSubject, failStreamOnError, retryStream, retryWithExponentialBackoff } from "./StreamApiUtils";

export type StreamApiStreamType =
	| WebSocketStreamType.GlobalStoreSnapshot
	| WebSocketStreamType.PermissionStream
	| WebSocketStreamType.StatusUpdateStream
	| WebSocketStreamType.ViewSnapshotDiffStream;

export type StreamApiResponse<T extends StreamApiStreamType> = T extends "StatusUpdateStream"
	? StackPackStateChangeEvent | StatusUpdateRequest
	: T extends "PermissionStream"
		? SubjectPermissionsResponse
		: T extends "ViewSnapshotDiffStream"
			? ViewSnapshotResponse
			: T extends "GlobalStoreSnapshot"
				? GlobalStoreSnapshot | GlobalStoreEvent
				: never;

export type StreamApiStartRequest<T extends StreamApiStreamType> = T extends "StatusUpdateStream"
	? StatusUpdateRequest
	: T extends "PermissionStream"
		? SubjectPermissionsRequest
		: T extends "ViewSnapshotDiffStream"
			? QueryStreamRequest
			: T extends "GlobalStoreSnapshot"
				? GlobalStoreRequest
				: never;

export interface StreamApi {
	startStream<T extends StreamApiStreamType>(
		streamType: T,
		request: StreamApiStartRequest<T>,
		config: WebUIConfig,
	): Observable<StreamApiResponse<T>>;
}

/**
 * Singleton
 */
class StreamApiService implements StreamApi {
	public static readonly URL = createURL({
		protocol: "WS",
		secure: location.protocol === "https:",
		host: location.hostname,
		port: location.port,
		path: "/api/stream",
	});

	// The individual streams are uniquely identified by streamIdentifiers
	private static lastStreamId = 0;

	private readonly socket: Lazy<WebSocketSubject<WebSocketInProtocol | WebSocketOutProtocol>> = Lazy.create(() =>
		createWebSocketSubject(StreamApiService.URL),
	);

	private static instance: StreamApiService;

	constructor() {
		if (StreamApiService.instance) {
			// biome-ignore lint/correctness/noConstructorReturn: shameful legacy
			return StreamApiService.instance;
		}
		StreamApiService.instance = this;
	}

	startStream<T extends StreamApiStreamType>(
		streamType: T,
		request: StreamApiStartRequest<T>,
		config: WebUIConfig,
	): Observable<StreamApiResponse<T>> {
		const streamIdentifier = `${++StreamApiService.lastStreamId}`;
		const logger = Logger.namespace(`${streamType}::${streamIdentifier}`);
		const retryDelay = config.streamReconnectTimeout * 1000;
		const startRequest: WebSocketStartStream = {
			_type: Type.WebSocketStartStream,
			streamType,
			streamIdentifier,
			request,
		};
		const stopRequest: WebSocketStopStream = {
			_type: Type.WebSocketStopStream,
			streamType,
		};

		const filterMessages = (message: WebSocketOutProtocol): boolean =>
			message.streamIdentifier === streamIdentifier && message.streamType === streamType;

		return this.socket
			.get()
			.multiplex(
				() => {
					logger.debug("Sending start request");

					return startRequest;
				},
				() => {
					logger.debug("Sending stop request");

					return stopRequest;
				},
				filterMessages,
			)
			.pipe(
				map(({ response }) => response as StreamApiResponse<T>),
				tap((message) => logger.debug("Received data", message)),
				retryStream(retryDelay, (wasOnline) =>
					logger.warn(
						wasOnline
							? `Server connectivity issues, retrying in ${retryDelay}ms`
							: "Network connection issues, retrying connection when network comes back",
					),
				),
				failStreamOnError(),
				retryWithExponentialBackoff(5, retryDelay),
			);
	}
}

export const streamApiService = new StreamApiService();
